import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import { Link, useSearchParams } from 'react-router-dom';
import { getInventoryItems, getStockBatches, getItemTypes, getWarehouses } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Search, ExternalLink, Boxes, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTS = ['all', 'in_stock', 'in_transit', 'assigned', 'deployed', 'inactive', 'returned', 'replaced', 'scrapped', 'lost'];
const STATUS_COLORS = {
  in_stock: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-amber-100 text-amber-700',
  assigned: 'bg-indigo-100 text-indigo-700',
  deployed: 'bg-green-100 text-green-700',
  inactive: 'bg-orange-100 text-orange-700',
  returned: 'bg-slate-200 text-slate-700',
  replaced: 'bg-purple-100 text-purple-700',
  scrapped: 'bg-red-100 text-red-700',
  lost: 'bg-red-200 text-red-800',
};

const InventoryItemsList = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all';
  const initialType = searchParams.get('item_type') || 'all';
  const initialCategory = searchParams.get('category') || 'all';
  const initialTab = searchParams.get('tab') === 'bulk' ? 'bulk' : 'serialized';

  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    (async () => {
      try {
        const [i, b, t, w] = await Promise.all([getInventoryItems({ limit: 1000 }), getStockBatches({}), getItemTypes(), getWarehouses()]);
        setItems(i.data); setBatches(b.data); setItemTypes(t.data); setWarehouses(w.data);
      } catch { toast.error('Failed to load inventory'); }
    })();
  }, []);

  const typeName = (id) => itemTypes.find(t => t.id === id)?.name || '-';
  const typeCategory = (id) => itemTypes.find(t => t.id === id)?.category || '';

  const filteredItems = useMemo(() => {
    return items.filter(i => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (typeFilter !== 'all' && i.item_type_id !== typeFilter) return false;
      if (categoryFilter !== 'all' && typeCategory(i.item_type_id) !== categoryFilter) return false;
      if (search && !(i.serial_number || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, statusFilter, typeFilter, categoryFilter, search, itemTypes]);

  const filteredBatches = useMemo(() => {
    return batches.filter(b => {
      if (typeFilter !== 'all' && b.item_type_id !== typeFilter) return false;
      if (categoryFilter !== 'all' && typeCategory(b.item_type_id) !== categoryFilter) return false;
      return true;
    });
  }, [batches, typeFilter, categoryFilter, itemTypes]);

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inventory Items</h1>
            <p className="text-sm text-slate-500">All serialized assets & bulk stock batches</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="serialized" data-testid="tab-serialized"><QrCode className="h-4 w-4 mr-1" />Serialized ({items.length})</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk"><Boxes className="h-4 w-4 mr-1" />Bulk Batches ({batches.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="serialized">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-2 top-3 h-4 w-4 text-slate-400" />
                    <Input className="pl-8" placeholder="Search serial number..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-serial" />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All Status' : s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-48" data-testid="filter-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Item Types</SelectItem>
                      {itemTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium text-slate-600">Serial #</th>
                        <th className="text-left p-2 text-xs font-medium text-slate-600">Item Type</th>
                        <th className="text-left p-2 text-xs font-medium text-slate-600">Status</th>
                        <th className="text-left p-2 text-xs font-medium text-slate-600">Held By</th>
                        <th className="text-left p-2 text-xs font-medium text-slate-600">Last Updated</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map(i => (
                        <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-2 font-mono text-xs text-indigo-700">{i.serial_number}</td>
                          <td className="p-2">{typeName(i.item_type_id)}</td>
                          <td className="p-2"><span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[i.status] || 'bg-slate-100 text-slate-700'}`}>{(i.status || '').replace('_', ' ')}</span></td>
                          <td className="p-2 text-xs">
                            <p className="font-medium">{i.holder_name || '-'}</p>
                            <p className="text-slate-500 capitalize">{(i.holder_type || '').replace('_', ' ')}</p>
                          </td>
                          <td className="p-2 text-xs text-slate-500">{i.updated_at ? new Date(i.updated_at).toLocaleString() : '-'}</td>
                          <td className="p-2 text-right">
                            <Link to={`/inventory/items/${i.id}`} data-testid={`view-${i.id}`}><Button size="sm" variant="ghost"><ExternalLink className="h-3 w-3" /></Button></Link>
                          </td>
                        </tr>
                      ))}
                      {filteredItems.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500">No items match the filters</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk">
            <Card>
              <CardContent className="pt-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left p-2 text-xs font-medium text-slate-600">Item Type</th>
                    <th className="text-left p-2 text-xs font-medium text-slate-600">Batch Code</th>
                    <th className="text-right p-2 text-xs font-medium text-slate-600">Initial</th>
                    <th className="text-right p-2 text-xs font-medium text-slate-600">Remaining</th>
                    <th className="text-left p-2 text-xs font-medium text-slate-600">Held By</th>
                    <th className="text-left p-2 text-xs font-medium text-slate-600">Created</th>
                  </tr></thead>
                  <tbody>{filteredBatches.map(b => (
                    <tr key={b.id} className="border-b">
                      <td className="p-2 font-medium">{typeName(b.item_type_id)}</td>
                      <td className="p-2 font-mono text-xs">{b.batch_code || '-'}</td>
                      <td className="p-2 text-right">{b.quantity_initial}</td>
                      <td className="p-2 text-right font-semibold text-indigo-700">{b.quantity_remaining}</td>
                      <td className="p-2 text-xs">
                        <p className="font-medium">{b.holder_name || '-'}</p>
                        <p className="text-slate-500 capitalize">{(b.holder_type || '').replace('_', ' ')}</p>
                      </td>
                      <td className="p-2 text-xs text-slate-500">{b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}{filteredBatches.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500">No batches</td></tr>}</tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default InventoryItemsList;
